import { Grid2, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCurrentCompanyDetails } from '@/services/paymentsQuery';
import { useUser } from '@/contexts/user/UserContext';
import { hasSettingsAccess } from '@/utils/permissions';
import {
    DATA_LON,
    DATEV_LODAS_FILE,
    requiresManualSalaryFileUpload
} from '@/utils/constant';
import PaymentsAPIServiceV2 from '@/services/PaymentsServiceV2';
import {
    ImportSummary,
    SalaryImportCard
} from '@/pages/company/salary/SalaryImportCard';
import {
    DataLonImportResponse,
    DataLonImportResultDialog
} from '@/pages/company/salary/DataLonImportResultDialog';
import {
    TaxCardUploadResponse,
    TaxCardUploadResultDialog
} from '@/pages/company/salary/TaxCardUploadResultDialog';

// Client-side cap mirroring the backend. Exports for a single company are small
// in practice; this catches an accidental wrong-file pick before bytes go out.
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const summarizeDataLon = (response: DataLonImportResponse): ImportSummary => ({
    total: response.totalRecords,
    updated: response.processed,
    failed: response.failed,
    errors: response.errors
});

const summarizeDatevLodas = (
    response: TaxCardUploadResponse
): ImportSummary => ({
    total: response.totalRows,
    updated: response.successfulImports,
    failed: response.failedImports,
    // Employees are identified by Personalnummer only — never by name or IBAN.
    errors: response.errors.map((error) =>
        `${error.employeeNumber ?? ''} ${error.errorMessage}`.trim()
    )
});

/**
 * Dashboard entry point for the manual salary-file upload.
 *
 * Renders nothing for companies on an API-integrated salary system — they have
 * no file to upload, and the Dashboard should stay free of dead controls.
 */
export const SalaryFileUpload = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { user } = useUser();
    const { data: companyDetails } = useCurrentCompanyDetails();

    const salaryProvider = companyDetails?.data?.salaryProvider;

    // Importing bank details is company-wide configuration, so it is gated to
    // the same roles that can open Settings. Managers reach the Dashboard but
    // are scoped to their own departments and must not see this.
    if (
        !hasSettingsAccess(user?.role) ||
        !requiresManualSalaryFileUpload(salaryProvider)
    ) {
        return null;
    }

    return (
        <Grid2
            container
            size={12}
            mt={4}
            gap={2}
            sx={{
                // The stat row above is three `lg: 3.8` cards in a gap-spaced
                // flex container, so it stops short of full width: 3 × 3.8/12
                // (95%) plus the two 16px gaps between them. Matching that keeps
                // this card's right edge flush with the cards above. Below `lg`
                // those cards stack full-width, so this one does too.
                width: { xs: '100%', lg: `calc(95% + ${theme.spacing(4)})` }
            }}
        >
            {salaryProvider === DATA_LON && (
                <SalaryImportCard<DataLonImportResponse>
                    title={t('data_lon_upload_title')}
                    info={t('data_lon_upload_info')}
                    confirmationNote={t('data_lon_upload_confirmation_note')}
                    accept={{ 'text/csv': ['.csv'] }}
                    constraints={t('data_lon_upload_constraints')}
                    maxFileSizeBytes={MAX_FILE_SIZE_BYTES}
                    upload={(file, onProgress) =>
                        PaymentsAPIServiceV2.getInstance().uploadDataLonEmployeesFile(
                            file,
                            onProgress
                        )
                    }
                    summarize={summarizeDataLon}
                    renderResultDialog={({ open, result, onClose }) => (
                        <DataLonImportResultDialog
                            open={open}
                            uploadResult={result}
                            onClose={onClose}
                        />
                    )}
                    alerts={{
                        success: t('data_lon_upload_success'),
                        partial: t('data_lon_upload_partial_success'),
                        failed: t('data_lon_upload_failed')
                    }}
                />
            )}

            {salaryProvider === DATEV_LODAS_FILE && (
                <SalaryImportCard<TaxCardUploadResponse>
                    title={t('datev_lodas_upload_title')}
                    info={t('datev_lodas_upload_info')}
                    confirmationNote={t('datev_lodas_upload_confirmation_note')}
                    accept={{
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                            ['.xlsx']
                    }}
                    constraints={t('datev_lodas_upload_constraints')}
                    maxFileSizeBytes={MAX_FILE_SIZE_BYTES}
                    upload={(file, onProgress) =>
                        PaymentsAPIServiceV2.getInstance().uploadDatevLodasFile(
                            file,
                            onProgress
                        )
                    }
                    summarize={summarizeDatevLodas}
                    renderResultDialog={({ open, result, onClose }) => (
                        <TaxCardUploadResultDialog
                            open={open}
                            uploadResult={result}
                            onClose={onClose}
                        />
                    )}
                    alerts={{
                        success: t('datev_lodas_upload_success'),
                        partial: t('datev_lodas_upload_partial_success'),
                        failed: t('datev_lodas_upload_failed')
                    }}
                />
            )}
        </Grid2>
    );
};
